import {
    FacilityLevelOfCare,
    Estimate,
    Payor,
    Vob,
    VobClassificationsItem,
} from '@finpay-estimation/estimation-types';
import {
    FacilityPayerPlans,
    FacilityPayers,
    patientNotes,
} from '../../models/vob';
import {useNavigate} from 'react-router';
import {crmInboundErrorMessages} from '../../../shared/enums';
import {Utils} from '../../../shared/utils';
import {
    ContactIncome,
    PhoneType,
    PrimaryContact,
} from '../../../shared/model/primary-contact';
import {
    PayorRisk,
    TimingRisk,
    timingRiskNameLookup,
} from '../../../shared/model/timing-and-payor-risk';
import {TypeOfCare, typeOfCare} from '../../../shared/model/type-of-care';
import {IocConfig, vobPatientPostBody} from '../../models/patient';
import {showErrorStatus} from '../../../security/state/user-slice';
import {UserRolePage} from '../../../security/model/user-role-page';
import {RolePageNames} from '../../../security/model/role-page-names';
import {checkUserRolePage} from '../../../security/components/access-control';
import {createPatientIoc} from '../../state/vob-patient-thunk';
import {
    PatientCurrentInsurance, 
    PatientViewModel, 
    emptyPatientCurrentInsurance
} from '../../../patient/components/models/patient-view-model';
import {
    clearPatientIocStatus,
    setSelectedEncounter,
} from '../../../patient/state/patient-slice';
import {admissionsAdvisorUtils} from '../../utils/admission-advisor-utils';
import {
    loadSelectedVob,
    createSelectedVob,
    saveSelectedVob,
} from '../../../shared/state/vob-thunk';
import { getEstimatorFacilityPayers } from '../../state/vob-thunk';
import {emptyEstimate, EstimateRequestConfig, PayorLocRateLos} from '../../models/estimator';
import {admissionsAdvisorService} from '../../services/admissions-advisor-services';
import {
    loadSelectedEstimate,
    createSelectedEstimate,
    saveSelectedEstimate,
    saveSelectedEstimateSelfPay,
    createSelectedEstimateSelfPay,
} from '../../../shared/state/estimator-thunk';
import {clearFinPassStatus} from 'src/admissions-advisor/state/vob-patient-slice';
import _ from 'lodash';
import moment from 'moment';
import {UserProfile} from '../../../security/model/user-profile';
import {workFlowStatus} from '../../../patient/components/models/workflow-status';
import {PayerRow} from '../../../admin-configuration/models/payers';
import {AppDispatch} from '../../../shared/state/store';
import {ClientStatusCardViewModel} from '../../../shared/model/client-status-card';
import {
    getPatientDemographics,
    savePatient,
    savePatientCurrentInsurance,
} from '../../../patient/state/patient-thunk';
import {patientService} from '../../../patient/services/patient-service';
import {estimateService} from '../../../estimates/services/estimate-service';
import {ImplementationFacility} from '../../../implementation-specialist/components/implementation-clients/details/models/implementation-facility';

/** Master list names for OON resolution (lowercase for comparison) */
const OON_PAYOR_NAME = 'average out-of-network';
const OON_PLAN_NAME = 'all';

export const newPatientLaunch = async (
    existingPatient: boolean,
    existingPatientInfo: any | undefined,
    ptRecordResponse: any,
    queryParams: any,
    vobState: any,
    estimate: Estimate | null,
    emptyVob: Vob,
    vobClassificationsState: VobClassificationsItem[],
    currentClientId: number,
    payors: PayerRow[],
    userProfile: UserProfile,
    clientsLeadId: string,
    clientsAccountId: string,
    navigate: ReturnType<typeof useNavigate>,
    dispatch: AppDispatch,
    allClients: Map<number, ClientStatusCardViewModel>
) => {
    const paramId: number = -2;
    const AVG_OON_PAYOR_ID = 14;
    const AVG_OON_PAYOR_PLAN_ID = 33;

    const currentInsurancePayorsResult =
        await estimateService.getCurrentInsurancePayors(currentClientId);
    const currentInsurancePayors: Payor[] =
        !currentInsurancePayorsResult.hasErrors
            ? currentInsurancePayorsResult.entity
            : [];

    const ptRecord = Utils.deepClone(ptRecordResponse);
    const urlParams = new URLSearchParams(queryParams);
    let formedParams = Object.fromEntries(urlParams);
    let clonedVob = Utils.deepClone(vobState);
    let clonedEstimate: Estimate | Record<string, never> = estimate
        ? Utils.deepClone(estimate)
        : {};
    let patientState: vobPatientPostBody;
    let currentPatientId: number;
    let payor = emptyVob.payor;
    let plan = emptyVob.plan;
    formedParams.inboundPatient = 'new';
    if (!existingPatient) {
        patientState = {
            firstName: ptRecord.patient?.contact?.firstName
                ? ptRecord.patient?.contact?.firstName
                : '',
            lastName: ptRecord.patient?.contact?.lastName
                ? ptRecord.patient?.contact?.lastName
                : '',
            birthDate: ptRecord.patient?.contact?.birthDate
                ? ptRecord.patient?.contact?.birthDate
                : '',
            fpPatientId: undefined,
            middleName: ptRecord.patient?.contact?.middleName,
            advisorPatientBody: {
                email: ptRecord.patient?.contact?.email,
                primaryAddress: ptRecord.patient?.contact?.primaryAddress,
                phoneNumber: ptRecord.patient?.contact?.primPhoneNum,
            },
            clientId: ptRecord.clientId,
        };
        // check for required patient information before creating Patient records
        if (
            !patientState.firstName ||
            !patientState.lastName ||
            !patientState.birthDate
        ) {
            return handleGenericError(
                crmInboundErrorMessages.patientInfoError,
                dispatch,
                navigate
            );
        } else {
            // Save FinPass Patient
            const newFinPassPatientPayload: PatientViewModel =
                mapToPatientViewModel(patientState, ptRecord);
            const fpPatientResponse = await dispatch(
                savePatient({
                    patient: newFinPassPatientPayload,
                })
            );
            patientState.fpPatientId =
                fpPatientResponse?.payload?.patientDetails.patientId;
            currentPatientId =
                fpPatientResponse?.payload?.patientDetails.patientId;
        }
    } else {
        patientState = {
            firstName: existingPatientInfo?.contact?.firstName,
            lastName: existingPatientInfo?.contact?.lastName,
            birthDate:
                ptRecord.patient?.contact?.birthDate ||
                existingPatientInfo?.contact?.birthDate,
            fpPatientId: existingPatientInfo?.patientId,
            middleName:
                existingPatientInfo?.contact?.middleName ||
                ptRecord.patient?.contact?.middleName,
            advisorPatientBody: {
                email: ptRecord.patient?.contact?.email,
                primaryAddress: ptRecord.patient?.contact?.primaryAddress,
                phoneNumber: ptRecord.patient?.contact?.primPhoneNum,
            },
            advisorPatientId: existingPatientInfo.advisorPatientId,
            clientId: ptRecord?.clientId,
        };
        currentPatientId = existingPatientInfo.patientId;
        patientState.fpPatientId = existingPatientInfo.patientId;
    }

    // If the ptRecord Estimate contains LOCs we need gather the Classification Ids and get them matched to the master list
    if (ptRecord.estimate?.loc?.length > 0) {
        // 1. Get all classification IDs from ptRecord Estimate LOCs
        const ptRecordLOCClassificationIds = ptRecord.estimate.loc
            .map((item: any) => item.vobClassificationId)
            .filter((id: any) => id !== undefined && id !== null);

        // 2. Determine existing VOB classifications based on inNetwork flag
        const isInNetwork = ptRecord.vob?.payer?.inNetwork ?? false;

        // Grab any classifications on the ptRecord VOB
        const existingVobClassifications = isInNetwork
            ? ptRecord.vob?.inNetwVobClassifications
            : ptRecord.vob?.ooNetwVobClassifications;

        // 3. Filter out classification IDs already in the VOB classification list
        const newIdsToAdd = ptRecordLOCClassificationIds.filter(
            (id: number) =>
                !existingVobClassifications.some(
                    (obj: {vobClassificationId: any}) =>
                        obj.vobClassificationId === id
                )
        );

        // 4. Map new IDs into classification objects
        const newObjectsToAdd = newIdsToAdd.map((id: number) => ({
            vobClassificationId: id,
        }));

        if (!ptRecord.vob) {
            ptRecord.vob = {};
        }

        if (!Array.isArray(ptRecord.vob?.inNetwVobClassifications)) {
            ptRecord.vob.inNetwVobClassifications = [];
        }

        if (!Array.isArray(ptRecord.vob?.ooNetwVobClassifications)) {
            ptRecord.vob.ooNetwVobClassifications = [];
        }

        // 5. Append to the correct classification list
        if (isInNetwork) {
            ptRecord.vob.inNetwVobClassifications.push(...newObjectsToAdd);
        } else {
            ptRecord.vob.ooNetwVobClassifications.push(...newObjectsToAdd);
        }
    }

    const ptRecordVOB = admissionsAdvisorUtils.matchClassifications(
        ptRecord.vob,
        vobClassificationsState
    );

    const patientInsurance = ptRecord.patientInsurance?.[0];

    clonedVob.vob = admissionsAdvisorUtils.mapV3VOBs(
        clonedVob.vob,
        ptRecordVOB,
        patientInsurance
    );

    // Resolve payor/plan from patient.insurance (ptCurrIns) and from ptRecord.vob (vobPayorPlan), then set current insurance and VOB
    const isSelfPay =
        ptRecord?.patient?.insurance?.insuranceType === 'self-pay' &&
        (!ptRecord?.vob || Object.keys(ptRecord.vob).length === 0);

    let ptCurrIns: PayorPlanMatch | null = null;
    let vobPayorPlan: PayorPlanMatch | null = null;

    if (!isSelfPay && ptRecord?.patient?.insurance) {
        ptCurrIns = resolvePayorPlanFromNames(
            ptRecord.patient.insurance,
            currentInsurancePayors
        );
    }

    const vobPayorName = (ptRecord.vob?.payer?.payerName || ptRecord.vob?.payer?.payorName)?.trim();
    const vobPlanName = ptRecord.vob?.plan?.planName?.trim();
    if (vobPayorName && currentInsurancePayors?.length) {
        vobPayorPlan = resolvePayorPlanFromNames(
            { payorName: vobPayorName, planName: vobPlanName },
            currentInsurancePayors
        );
    }

    // V2 launch uses non-rate as payors with facility payors overlayed.

    // Set patient current insurance: self-pay | ptCurrIns | vobPayorPlan
    if (isSelfPay && payors?.length) {
        const selfPayMaster = payors.find((p) => p.payorId === 97);
        if (selfPayMaster) {
            const selfPayPlan = selfPayMaster.payorPlans?.[0];
            await dispatch(savePatientCurrentInsurance({
                insurance: {
                    ...emptyPatientCurrentInsurance,
                    patientId: currentPatientId,
                    payorId: 97,
                    payorPlanId: selfPayPlan?.payorPlanId ?? 0,
                    memberId: '',
                    groupNumber: '',
                },
            }));
        }
    } else if (
        ptCurrIns &&
        ptCurrIns.payor.payorName?.trim().toLowerCase() !== OON_PAYOR_NAME
    ) {
        const patientIns = ptRecord?.patient?.insurance;
        await dispatch(savePatientCurrentInsurance({
            insurance: {
                ...emptyPatientCurrentInsurance,
                patientId: currentPatientId,
                payorId: ptCurrIns.payor.payorId,
                payorPlanId: ptCurrIns.plan?.payorPlanId ?? 0,
                memberId: String(patientIns?.policyNumber ?? ''),
                groupNumber: String(patientIns?.groupNumber ?? ''),
            },
        }));
    } else if (vobPayorPlan) {
        await dispatch(savePatientCurrentInsurance({
            insurance: {
                ...emptyPatientCurrentInsurance,
                patientId: currentPatientId,
                payorId: vobPayorPlan.payor.payorId,
                payorPlanId: vobPayorPlan.plan?.payorPlanId ?? 0,
                groupNumber: clonedVob.vob.groupNum ?? '',
                memberId: clonedVob.vob.policyNum ?? '',
            },
        }));
    }

    // Set VOB payor/plan: if not self-pay prefer vobPayorPlan when not Average OON, else ptCurrIns
    if (isSelfPay && payors?.length) {
        const selfPayMaster = payors.find((p) => p.payorId === 97);
        if (selfPayMaster) {
            const selfPayPlan = selfPayMaster.payorPlans?.[0];
            payor = {
                payorId: 97,
                payorName: selfPayMaster.payorName,
                cfgPayorId: 97,
            } as any;
            plan = selfPayPlan
                ? {
                      payorPlanId: selfPayPlan.payorPlanId,
                      planName: selfPayPlan.planName,
                      cfgPayorPlanId: selfPayPlan.payorPlanId,
                  } as any
                : emptyVob.plan;
        }
    } else {
        const chosen =
            vobPayorPlan &&
            vobPayorPlan.payor.payorName?.trim().toLowerCase() !== OON_PAYOR_NAME
                ? vobPayorPlan
                : ptCurrIns;
        payor = chosen?.payor;
        plan = chosen?.plan;
    }

    const selectedClient = Array.from(allClients.values()).find(
        client => client.clientId === Number(currentClientId)
    );

    clonedVob.vob.client = {
        clientId: selectedClient?.clientId,
        clientName: selectedClient?.clientName!,
        clientList: [],
    };

    const selectedClientFacility = selectedClient?.clientFacilities?.find(
        cfacility => cfacility.facilityId === ptRecord.facilityId
    );

    clonedVob.vob.facility = {
        facilityId: selectedClientFacility?.facilityId,
        facilityName: selectedClientFacility?.facilityName!,
    };
    clonedVob.vob.patientNotes = formatVOBComments(ptRecord);
    clonedVob.vob.payor = payor;
    clonedVob.vob.plan = plan;
    clonedVob.vob.patientId = currentPatientId;
    clonedVob.vob.vobSourceId = 3;  // CRM
    clonedVob.vob.isValid = true;

    // HAWK TODO: REVISIT SELF PAY setting for new VOB.
    // clonedVob.vob.patientId = currentPatientId;
    // self pay vob sets is valid to true, and self pay to true
    // resets the policy dates as well
    // sets fields to 0 and empties out the vob classifications
    // if (clonedVob.vob.payor.cfgPayorId === 97) {
    //     // we need to set private pay flag
    
    // }

    let vobResponse: any = undefined;
    let vobResavedResponse: any = undefined;

    if (clonedVob.vob.payor.cfgPayorId !== 97) {
        vobResponse = await dispatch(createSelectedVob(clonedVob.vob));
        clonedVob.vob = vobResponse?.payload;
    }
    clonedEstimate.anticipatedAdmitDate = ptRecord.admissionDt
        ? ptRecord.admissionDt
        : null;

    const today = Utils.convertISODateAsUTC(new Date().toISOString());
    clonedEstimate.description = `No LOC - ${today}`;

    const postEstimateBody = admissionsAdvisorUtils.mapToNewv3Estimate(
        vobResponse?.payload?.vobId,
        ptRecord.clientId,
        ptRecord.facilityId,
        clonedEstimate,
        patientState.advisorPatientId!,
        patientState.fpPatientId!,
        {
            payor: {
                payorId: payor.payorId,
                facilityPayorId: payor.facilityPayorId,
                payorName: payor.payorName,
                facilityPayorName: payor.facilityPayorName || payor.payorName,
                inNetwork: payor.inNetwork || false
            },
            plan: {
                payorPlanId: plan.payorPlanId,
                facilityPlanId: plan.facilityPlanId,
                planName: plan.planName,
                facilityPlanName: plan.facilityPlanName || plan.planName
            },
            isOverridden: false
        }
    );

    let updatedPostEstimateBody = _.omit(postEstimateBody, [
        'priorCare',
        'financialAssistance',
    ]);

    // if we have everything we need to populate loc
    // fetch facility locs by the plan id, exactly like we do in the estimator
    // check the names of the loc provided by the patient record call
    // set the facilityLevelOfCare in the estimate
    if (
        vobResponse &&
        plan.facilityPlanId &&
        ptRecord.estimate?.loc &&
        ptRecord.estimate?.loc?.length > 0
    ) {
        const response = await admissionsAdvisorService.getFacilityLevelsOfCare(
            ptRecord.facilityId,
            plan.facilityPlanId
        );

        const facilityLevelOfCare: FacilityLevelOfCare[] = (
            response.entity as FacilityLevelOfCare[]
        )?.filter((loc: FacilityLevelOfCare) => {
            return ptRecord.estimate?.loc?.some((crmLoc: any) => {
                return crmLoc.locName === loc.facilityLevelOfCareName;
            });
        });

        // Ensure the VOB includes all classification IDs from each Level of Care in the estimate.
        // Step 1: Extract all classification IDs from the estimate's LOCs.
        const LOCClassificationIds: number[] = facilityLevelOfCare
            .map(item => item.vobClassificationId)
            .filter((id): id is number => id !== undefined && id !== null);

        // 2. Take all of these LOCClassificationIds and match them in the list facility classification
        const fullMatchedClassifications =
            admissionsAdvisorUtils.matchClassificationsById(
                LOCClassificationIds,
                vobClassificationsState
            );
        // 3. Determine network status
        const isInNetwork = clonedVob.vob?.payer?.inNetwork ?? false;

        // 4. Grab existing classifications
        const existingVobClassifications = isInNetwork
            ? clonedVob.vob?.inNetwVobClassifications || []
            : clonedVob.vob?.ooNetwVobClassifications || [];

        // 5. Get IDs of existing classifications to avoid duplicates
        const existingIds = new Set(
            existingVobClassifications.map(
                (id: {vobClassificationId: number}) => id.vobClassificationId
            )
        );

        // Filter new ones not already in the existing list
        const newClassificationsToAdd = fullMatchedClassifications.filter(
            (c: any) => !existingIds.has(c.vobClassificationId)
        );
        const updatedVobClassifications = [
            ...existingVobClassifications,
            ...newClassificationsToAdd,
        ];

        // 6. Assign back to VOB and save
        const updatedVob = {
            ...clonedVob.vob,
            // advisorPatientId: patientState.advisorPatientId,
            patientId: currentPatientId,
            ...(isInNetwork
                ? {inNetwVobClassifications: updatedVobClassifications}
                : {ooNetwVobClassifications: updatedVobClassifications}),
        };

        if(!updatedVob.isCarryover) {
            updatedVob.isCarryover = false
        }
        const updatedVobId = updatedVob.vobId;
        delete updatedVob.vobId;
        vobResavedResponse = await dispatch(saveSelectedVob({ 
            vobId: updatedVobId,
            vob: updatedVob 
        }));

        updatedPostEstimateBody.facilityLevelOfCare = facilityLevelOfCare;

        const levelsOfCare = updatedPostEstimateBody?.facilityLevelOfCare
            ?.map((loc: FacilityLevelOfCare) => loc.facilityLevelOfCareName)
            .join(', ');

        const estimateDescription = `${levelsOfCare} - ${today}`;

        updatedPostEstimateBody.description = estimateDescription;
    }
    updatedPostEstimateBody.patientId = currentPatientId;
    delete updatedPostEstimateBody.advisorPatientId
    if (payor?.payorId === 97) {
        updatedPostEstimateBody.isSelfPay = true;
    }
    const savedEstimateResponse =
        payor?.payorId === 97
            ? await dispatch(createSelectedEstimateSelfPay(updatedPostEstimateBody))
            : await dispatch(createSelectedEstimate(updatedPostEstimateBody));

    // @ts-ignore
    if (savedEstimateResponse?.error?.message) {
        return handleGenericError(
            crmInboundErrorMessages.saveEstimateError,
            dispatch,
            navigate
        );
    }

    // Save Instance of Care
    const iocConfig = mapToIOCModel(
        clonedVob,
        savedEstimateResponse?.payload,
        currentPatientId,
        savedEstimateResponse?.payload?.estimateId,
        ptRecord,
        queryParams,
        userProfile,
        clientsLeadId,
        clientsAccountId
    );
    patientState.birthDate = moment
        .utc(patientState.birthDate)
        .format('YYYY-MM-DD'); // adjusting birthdate for vob input field

    const iocResponse: any = await dispatch(createPatientIoc(iocConfig));
    if (iocResponse?.payload?.patientEncounterId) {
        let putEstimateBody = admissionsAdvisorUtils.mapToNewv3Estimate(
            vobResponse?.payload?.vobId,
            ptRecord.clientId,
            ptRecord.facilityId,
            savedEstimateResponse?.payload,
            patientState.advisorPatientId!,
            patientState.fpPatientId!,
            {
                payor: {
                    payorId: payor.payorId,
                    facilityPayorId: payor.facilityPayorId,
                    payorName: payor.payorName,
                    facilityPayorName: payor.facilityPayorName || payor.payorName,
                    inNetwork: payor.inNetwork || false
                },
                plan: {
                    payorPlanId: plan.payorPlanId,
                    facilityPlanId: plan.facilityPlanId,
                    planName: plan.planName,
                    facilityPlanName: plan.facilityPlanName || plan.planName
                },
                isOverridden: false
            },
            iocResponse?.payload?.patientEncounterId
        );
        // Save Estimate conditionally if self-pay
        const estimatePayload = {
            estimate: putEstimateBody,
            estimateId: savedEstimateResponse?.payload?.estimateId,
        };
        const putEstimateResponse =
            payor.payorId === 97
                ? await dispatch(saveSelectedEstimateSelfPay(estimatePayload))
                : await dispatch(saveSelectedEstimate(estimatePayload));          

        // @ts-ignore
        if (putEstimateResponse?.error?.message) {
            return handleGenericError(
                crmInboundErrorMessages.saveEstimateError,
                dispatch,
                navigate
            );
        }
    }

    dispatch(clearPatientIocStatus());
    dispatch(clearFinPassStatus());

    let redirectPath: string;
    if (!vobResponse) {
        redirectPath = `/patients/${iocResponse?.payload?.patientId}/estimate/${savedEstimateResponse?.payload?.estimateId}${formedParams.op === 'pay' ? '?op=pay' : ''}`;
    } else {
        let isValid = false;
        // Use the most recent VOB response to determine validity
        if (vobResavedResponse !== undefined) {
            isValid = vobResavedResponse?.payload?.isValid;
        } else {
            isValid = vobResponse?.payload?.isValid;
        }
        redirectPath = isValid
            ? `/patients/${iocResponse?.payload?.patientId}/estimate/${savedEstimateResponse?.payload?.estimateId}${formedParams.op === 'pay' ? '?op=pay' : ''}`
            : `/patients/${iocResponse?.payload?.patientId}/vob/${vobResponse?.payload?.vobId}${formedParams.op === 'pay' ? '?op=pay' : ''}`;
    }

    window.location.href = redirectPath; // redirect with hard reload
};

const handleGenericError = (
    errorMessage: string,
    dispatch: AppDispatch,
    navigate: ReturnType<typeof useNavigate>
) => {
    dispatch(showErrorStatus(errorMessage));
    navigate(`/patients`);
};

/** Payor + plan match from master/facility used for V2 launch current insurance and VOB */
interface PayorPlanMatch {
    payor: any;
    plan: any;
}

function resolveOonPayorPlanByName(
    payors: Payor[] | null | undefined
): PayorPlanMatch | null {
    if (payors == null) return null;
    if (!payors.length) return null;
    const matches = payors.filter(
        (p) => p.payorName?.trim().toLowerCase() === OON_PAYOR_NAME
    );
    if (!matches.length) return null;
    const masterPayor = matches[0];
    const masterPlan = masterPayor.plans?.find(
        (pl) => pl.planName?.trim().toLowerCase() === OON_PLAN_NAME
    );
    if (!masterPayor || !masterPlan) return null;
    return {
        payor: {
            payorId: masterPayor.payorId,
            payorName: masterPayor.payorName,
            cfgPayorId: masterPayor.payorId,
        } as any,
        plan: {
            payorPlanId: masterPlan.payorPlanId,
            planName: masterPlan.planName,
            cfgPayorPlanId: masterPlan.payorPlanId,
        } as any,
    };
}

/**
 * Resolve payor/plan from patient.insurance using client current-insurance payors (name match, first row).
 * Does not filter by payorSource / VOB source id.
 */
function resolveClientPayorPlan(
    patientInsurance: { payorName?: string; planName?: string } | null | undefined,
    payors: Payor[]
): PayorPlanMatch | null {
    if (!patientInsurance || !payors?.length) return null;
    const payorName = patientInsurance.payorName?.trim();
    const planName = patientInsurance.planName?.trim();
    if (!payorName) return null;
    const matches = payors.filter(
        (p) => p.payorName?.trim().toLowerCase() === payorName.toLowerCase()
    );
    if (!matches.length) return null;
    const masterPayor = matches[0];
    const masterPlan = planName
        ? masterPayor.plans?.find(
              (pl) => pl.planName?.trim().toLowerCase() === planName.toLowerCase()
          )
        : masterPayor.plans?.[0];
    const payor = {
        payorId: masterPayor.payorId,
        payorName: masterPayor.payorName,
        cfgPayorId: masterPayor.payorId,
    } as any;
    const plan = masterPlan
        ? {
              payorPlanId: masterPlan.payorPlanId,
              planName: masterPlan.planName,
              cfgPayorPlanId: masterPlan.payorPlanId,
          }
        : ({ payorPlanId: 0, planName: '', cfgPayorPlanId: 0 } as any);
    return { payor, plan };
}

/**
 * Resolve payor/plan from payor/plan names (via client current-insurance payors, then OON + ALL by name).
 * Returns null only if all paths fail (e.g. empty payors or OON not on master list).
 */
function resolvePayorPlanFromNames(
    patientInsurance: { payorName?: string; planName?: string } | null | undefined,
    payors: Payor[]
): PayorPlanMatch | null {
    return (
        resolveClientPayorPlan(patientInsurance, payors) ??
        resolveOonPayorPlanByName(payors)
    );
}

function mapToPatientViewModel(
    vobPatientRecord: vobPatientPostBody,
    patientRecord: any
): PatientViewModel {
    const patientContact: PrimaryContact = {
        title: patientRecord.patient?.contact?.title || undefined!,
        firstName: vobPatientRecord.firstName,
        middleName:
            patientRecord.patient?.contact?.middleName ||
            vobPatientRecord.middleName,
        lastName: vobPatientRecord.lastName,
        primaryAddress: {
            streetAddress1:
                vobPatientRecord.advisorPatientBody?.primaryAddress
                    ?.streetAddress1!,
            streetAddress2:
                vobPatientRecord.advisorPatientBody?.primaryAddress
                    ?.streetAddress2!,
            city: vobPatientRecord.advisorPatientBody?.primaryAddress?.city!,
            state: {
                stateId:
                    vobPatientRecord.advisorPatientBody?.primaryAddress?.state
                        ?.stateId!,
                stateCode:
                    vobPatientRecord.advisorPatientBody?.primaryAddress?.state
                        ?.stateCode!,
                stateName:
                    vobPatientRecord.advisorPatientBody?.primaryAddress?.state
                        ?.stateName!,
            },
            zipCode:
                vobPatientRecord.advisorPatientBody?.primaryAddress?.zipCode!,
            addressType:
                patientRecord.patient?.contact?.primaryAddress?.addressType ||
                'Primary',
        },
        email: vobPatientRecord.advisorPatientBody?.email!,
        primPhoneNum: vobPatientRecord.advisorPatientBody?.phoneNumber!,
        primPhoneType:
            patientRecord.patient?.contact?.primPhoneType || PhoneType.home,
        secPhoneNum: patientRecord.patient?.contact?.secPhoneNum,
        secPhoneType:
            patientRecord.patient?.contact?.secPhoneType || PhoneType.mobile,
        leaveVMFl: patientRecord.patient?.contact?.leaveVMFl || false,
        smsConsentFl: patientRecord.patient?.contact?.smsConsentFl || false,
        ssnLast4: patientRecord.patient?.contact?.ssnLast4 || '',
        birthDate:
            patientRecord.patient?.contact?.birthDate ||
            vobPatientRecord.birthDate,
        contactIncome: {
            isEmployed:
                patientRecord.patient?.contact?.contactIncome?.isEmployed ||
                false,
            yearlySalary:
                patientRecord.patient?.contact?.contactIncome?.yearlySalary ||
                0,
            employer:
                patientRecord.patient?.contact?.contactIncome?.employer || '',
            yearsAtEmployer:
                patientRecord.patient?.contact?.contactIncome
                    ?.yearsAtEmployer || 0,
        } as ContactIncome,
    };

    const newPatient = {
        patientId: 0,
        contact: patientContact,
        patientStatus: 'NEW',
        clientId: patientRecord.clientId.toString(),
    } as PatientViewModel;

    return newPatient;
}

export const patientRelaunch = async (
    clientsPatientIOCIdFilter: number[],
    ptRecordResponse: any,
    queryParams: any,
    userProfile: any,
    dispatch: AppDispatch,
    navigate: ReturnType<typeof useNavigate>,
    payors: any,
    currentClientId: number,
    originalPtRecordResponse: any,
    facilities: ImplementationFacility[][],
    emptyVob: Vob,
    allClients: Map<number, ClientStatusCardViewModel>,
    vobClassificationsState: VobClassificationsItem[],
    setChangeSummary: (summary: any) => void,
    setConfirmChangesModalOpen: (val: boolean) => void,
    setModalCallback: (cb: (res: ModalResponse) => void) => void,
    clientsLeadId: string,
    clientsAccountId: string
) => {
    let redirectURLPath: string = '/admissions-advisor';
    let ptRecordClone = Utils.deepClone(ptRecordResponse);
    const urlParams = new URLSearchParams(queryParams);
    let formedParams = Object.fromEntries(urlParams);
    const combinedParams = {
        ...ptRecordClone,
        op: formedParams.op ? formedParams.op : undefined,
    };
    const estimasteRoleStatus = hasEstimatesRole(userProfile);
    // First Get IOC
    const instanceOfCare = await patientService.getPatientInstanceOfCare({
        patientId: 0,
        encounterId: clientsPatientIOCIdFilter[0],
    });
    let getAttachedEstimate: any;
    let vobResult: any;
    const paramId: number = -2;
    let opPay = false;
    if (formedParams.op === 'pay') {
        if (!estimasteRoleStatus) {
            const iocRoleStatus = hasIOCRole(userProfile);
            if (iocRoleStatus) {
                redirectURLPath = `/specialist/dashboard/${instanceOfCare.entity.patientId}`;
            } else {
                redirectURLPath = '/accessdenied';
            }
        }else{
           opPay = true;
        }
    }

    if (instanceOfCare.entity.pfrEstimateId) {
        const config: EstimateRequestConfig = {
            paramId: paramId,
            estimationId: instanceOfCare.entity.pfrEstimateId,
        };

        getAttachedEstimate = await dispatch(loadSelectedEstimate(config));

        if (getAttachedEstimate.error?.message) {
            return handleGenericError(
                crmInboundErrorMessages.newEstimateIdError,
                dispatch,
                navigate
            );
        }

        if (getAttachedEstimate.payload.vobId) {
            const vobConfig: any = {
                paramId: paramId,
                vobId: getAttachedEstimate.payload.vobId,
            };
            vobResult = await dispatch(
                loadSelectedVob(vobConfig)
            );
        }
    }

    let selectedIoc = instanceOfCare.entity;
    let facilityId = selectedIoc.facilityId || ptRecordClone.facilityId;
    ptRecordClone.inboundPatient = undefined;

    const sssconfig: any = {
        paramId: paramId,
        masterListPayers: payors,
        facilityId: facilityId,
    };

    const facilityPayers = await dispatch(
        getEstimatorFacilityPayers(sssconfig)
    ); //facilityPayers

    let savedClientId = currentClientId;
    let handleChange = false;
    let isConverted = false;
    let hasCRMChanged = {
        facilityChange: false,
        payerChange: false,
    };
    let confirmPtChanges = false;
    // Only do the changes check - if a Facility ID is present.
    // Checking to see if there are changes in facility or payor from ptRecord vs saved VOB
    const ptRecordValid = !!(
        originalPtRecordResponse?.facilityId ||
        originalPtRecordResponse?.vob?.payer?.payerName
    );

    let facilityPayersResponse: any = [];
    const newFacilityId =
        originalPtRecordResponse?.facilityId ??
        vobResult?.payload?.facility.facilityId;

    if (ptRecordValid) {
        const adjustedFacility =
            originalPtRecordResponse?.facilityId ??
            vobResult?.payload?.facility.facilityId;
        const adjustedPayor =
            originalPtRecordResponse?.vob?.payer?.payorName ??
            vobResult?.payload?.payer?.payorName;

        hasCRMChanged = checkForChanges(
            adjustedFacility, // PtRecord Facility if exists
            vobResult?.payload?.facility.facilityId, // Existing VOB Facility
            adjustedPayor, // PtRecord payor if exists
            vobResult?.payload?.payer?.payorName // ?? vobResult?.payload?.vobBody?.payer?.facilityPayorName // Existing payor (facility)
        );

        handleChange =
            hasCRMChanged.facilityChange || hasCRMChanged.payerChange;
        isConverted = selectedIoc.workflow.workflowId === 3;

        const config: any = {
            paramId: paramId,
            masterListPayers: payors,
            facilityId: newFacilityId,
        };
        // call GET all facility payors
        facilityPayersResponse = await dispatch(
            getEstimatorFacilityPayers(config)
        );
    }

    let vobPayload: any = Utils.deepClone(vobResult?.payload || emptyVob);
    let updatedEstimate = Utils.deepClone(getAttachedEstimate.payload);

    if (handleChange && !isConverted) {
        let updatedIOC = Utils.deepClone(selectedIoc);
        let matchingPayor;
        //  Setting payor ID based on change selection
        const newPayorName = hasCRMChanged.payerChange
            ? ptRecordClone?.vob?.payer?.payerName
            : vobResult?.payload?.payer?.payorName;

        const newFacilityInfo = facilities
            ?.flat()
            .find(fac => fac?.facilityId === newFacilityId);

        if (hasCRMChanged.payerChange) {
            matchingPayor = facilityPayersResponse?.payload.find(
                (row: any) => row.facilityPayorName === newPayorName
            );
            // If there is no matching payor in the facility we need to default to Average OON
            if (!matchingPayor) {
                const newPayorToSave = facilityPayersResponse?.payload.find(
                    (el: FacilityPayers) => el.cfgPayorId === 14
                )!;
                // check to see if Average OON exists / if it doesn't leave it blank
                if (newPayorToSave) {
                    matchingPayor = newPayorToSave;
                }
            }
        }

        setChangeSummary({
            facilityChange: {
                hasChanged: hasCRMChanged.facilityChange,
                prevId: vobResult?.payload?.facility.facilityId,
                prevName: vobResult?.payload?.facility.facilityName,
                newId: newFacilityInfo?.facilityId!,
                newName: newFacilityInfo?.facilityName!,
            },
            payerChange: {
                hasChanged: hasCRMChanged.payerChange,
                prevId: vobResult?.payload?.payer?.facilityPayorId,
                prevName: vobResult?.payload?.payer?.payorName,
                newId: matchingPayor?.facilityPayorId,
                newName: matchingPayor?.facilityPayorName,
                ptRecordName: ptRecordClone?.vob?.payer?.payerName,
            },
            clientId: savedClientId,
        });

        // Open modal to confirm whether the user wants to save the detected changes
        const modalResult = await new Promise<ModalResponse>(resolve => {
            setConfirmChangesModalOpen(true);
            setModalCallback(() => resolve);
        });

        // User triggered a change confirmation
        if (modalResult.confirmed) {
            confirmPtChanges = true;
            // Setting Up Calls to make changes
            // Setting Facility we need to save
            const facilityToSave = facilities
                ?.flat()
                .find(fac => fac?.facilityId === newFacilityInfo?.facilityId);

            // The following changes need to be applied
            // in VOB
            // -- Changing Facility
            vobPayload.facility = {
                facilityId: facilityToSave?.facilityId,
                facilityName: facilityToSave?.facilityName,
            };

            // changing vob object to have payorId for the comparePayers utils below
            vobPayload.payer = {
                ...vobPayload.payer,
                payorId: hasCRMChanged.payerChange
                    ? matchingPayor?.facilityPayorId
                    : vobPayload.payer?.payorId,
                payorName: hasCRMChanged.payerChange
                    ? matchingPayor?.facilityPayorName
                    : vobPayload.payer?.payorName,
            };

            // -- Change payor / plan
            if (facilityPayersResponse?.payload) {
                const facilityPayers = [
                    ...(facilityPayersResponse.payload as FacilityPayers[]),
                ].sort((a, b) =>
                    a.facilityPayorId! > b.facilityPayorId! ? 1 : -1
                );
                let payorToSave: FacilityPayers | null;
                const matchedPayor: FacilityPayers | null =
                    admissionsAdvisorUtils.compareFacilityPayorId(
                        facilityPayers,
                        vobPayload
                    );

                // is there a matched payor based on ptRecord change?
                if (matchedPayor) {
                    payorToSave = matchedPayor;
                } else {
                    // Setting default payor Avg OOO (14) if there is no match within the facility
                    payorToSave = facilityPayers?.find(
                        (el: FacilityPayers) => el.cfgPayorId === 14
                    )!;
                }

                // This means there's no average out of network leaving blank
                if (!payorToSave) {
                    vobPayload.payer = {...emptyVob.payor};
                    vobPayload.plan = {...emptyVob.plan};
                } else {
                    // we are setting the new payor
                    vobPayload.payer = {
                        payorId: payorToSave?.cfgPayorId,
                        payorName: payorToSave?.facilityPayorName,
                        payorCode: payorToSave?.facilityPayorCode,
                        inNetwork: payorToSave?.inNetwork,
                        payersList: undefined,
                    };

                    // setting correct plan name
                    const newPlanName: string =
                        originalPtRecordResponse?.vob?.plan?.planName ?? '';
                    vobPayload.plan.planName = newPlanName
                        ? newPlanName
                        : vobPayload.plan.planName;

                    let planToSave: FacilityPayerPlans | null;
                    // if it had a previous plan we need match
                    if (vobPayload.plan.planName) {
                        planToSave = admissionsAdvisorUtils.comparePlansByName(
                            payorToSave,
                            vobPayload
                        );
                    } else {
                        planToSave = payorToSave.facilityPayorPlans?.find(
                            (el: FacilityPayerPlans) => el.cfgPayorPlanId === 33
                        )!;
                    }

                    if (!planToSave) {
                        // if no plan 'ALL' is found, reset plan
                        vobPayload.plan = {...emptyVob.plan};
                    } else {
                        // we are filling the VOB with the new plan
                        vobPayload.plan = {
                            payorPlanId: planToSave?.cfgPayorPlanId,
                            planName: planToSave?.facilityPayorPlanName,
                            planCode: planToSave?.facilityPayorPlanCode,
                            facilityPlanId: planToSave?.facilityPayorPlanId,
                            pdrRate: (
                                planToSave?.facilityPayorLocRateLos as PayorLocRateLos
                            )?.pdrRate,
                            losDays: (
                                planToSave?.facilityPayorLocRateLos as PayorLocRateLos
                            )?.losDays,
                            payersPlanList: facilityPayers,
                        };
                    }
                }
            }
            // in Estimate
            updatedEstimate.facilityLevelOfCare = []; // Clear LOC
            updatedEstimate.facilityId = newFacilityId; // Change facilityId
            // in IOC
            updatedIOC.facilityId = newFacilityId; // Change facilityId
            updatedIOC.facilityName = newFacilityInfo?.facilityName; // Change Facility Name
        }
    }

    if (facilityId && ptRecordClone) {
        let vob: Vob = vobPayload;
        let payor = vob.payor || emptyVob.payor;
        let plan = vob.plan || emptyVob.plan;
        let client = vob.client || emptyVob.client;
        let facility = vob.facility || emptyVob.facility;

        // if existing VOB is missing client information we need to build it
        if (vob.client.clientId) {
            client = vob.client;
        } else {
            const selectedClient = Array.from(allClients.values()).find(
                client => client.clientId === Number(currentClientId)
            );
            client = {
                clientId: savedClientId,
                clientName: selectedClient?.clientName!,
            };
        }

        // if existing VOB is missing facility information we need to build it
        if (vob.facility.facilityId) {
            facility = vob.facility;
        } else {
            const selectedClient = Array.from(allClients.values()).find(
                client => client.clientId === Number(currentClientId)
            );
            const clientFacilities = selectedClient?.clientFacilities?.find(
                cfacility => cfacility.facilityId === facilityId
            );
            facility = {
                facilityId: clientFacilities?.facilityId!,
                facilityName: clientFacilities?.facilityName!,
            };
        }

        const patientInsurance = ptRecordClone.patientInsurance?.[0];

        // Only merge patient record VOB into existing VOB when the response included a vob object.
        // Otherwise we would overwrite existing VOB classification coinsurance with empty/default values.
        const hadVobInResponse = ptRecordClone.vob != null;

        if (hadVobInResponse) {
            // Need to update the ptRecord VOB - if it doesnt exist we need to add it
            if (!ptRecordClone.vob) {
                ptRecordClone.vob = {};
            }

            if (!Array.isArray(ptRecordClone.vob?.inNetwVobClassifications)) {
                ptRecordClone.vob.inNetwVobClassifications = [];
            }

            if (!Array.isArray(ptRecordClone.vob?.ooNetwVobClassifications)) {
                ptRecordClone.vob.ooNetwVobClassifications = [];
            }

            // 1. Map estimate classification IDs from getAttachedEstimate
            const estimateIds = Array.isArray(
                getAttachedEstimate?.payload?.facilityLevelOfCare
            )
                ? getAttachedEstimate.payload.facilityLevelOfCare
                      .map((item: any) => item.vobClassificationId)
                      .filter((id: any) => id !== undefined && id !== null)
                : [];

            // 2. Map classification IDs from ptRecord estimate LOCs
            const ptRecordEstimateIds = Array.isArray(ptRecordClone.estimate?.loc)
                ? ptRecordClone.estimate.loc
                      .map((item: any) => item.vobClassificationId)
                      .filter((id: any) => id !== undefined && id !== null)
                : [];

            // 3. Merge and dedupe all classification IDs
            const uniqueVOBClassificationIds = Array.from(
                new Set([...estimateIds, ...ptRecordEstimateIds])
            );
            const existingVobClassifications = payor.inNetwork
                ? ptRecordClone.vob.inNetwVobClassifications
                : ptRecordClone.vob.ooNetwVobClassifications;
            const newIdsToAdd = uniqueVOBClassificationIds.filter(
                id =>
                    !existingVobClassifications.some(
                        (obj: {vobClassificationId: any}) =>
                            obj.vobClassificationId === id
                    )
            );
            const newObjectsToAdd = newIdsToAdd.map(id => ({
                vobClassificationId: id,
            }));

            // 4. Append to the correct classification list
            if (payor.inNetwork) {
                ptRecordClone.vob.inNetwVobClassifications.push(...newObjectsToAdd);
            } else {
                ptRecordClone.vob.ooNetwVobClassifications.push(...newObjectsToAdd);
            }

            const ptRecordVOB = admissionsAdvisorUtils.matchClassifications(
                ptRecordClone.vob,
                vobClassificationsState
            );

            vob = admissionsAdvisorUtils.mapV3VOBs(
                vob,
                ptRecordVOB,
                patientInsurance
            );
        }

        vob.patientId = instanceOfCare.entity.patientId;

        if (
            ptRecordClone?.patient?.insurance?.insuranceType === 'self-pay' &&
            (!ptRecordClone?.vob || Object.keys(ptRecordClone.vob).length === 0)
        ) {
            const selfPayPayor = getSelfPayPayer(facilityPayers);
            if (selfPayPayor) {
                payor = {
                    ...selfPayPayor,
                    payorId: selfPayPayor.cfgPayorId,
                    payorName: selfPayPayor.facilityPayorName,
                    payorCode: selfPayPayor.facilityPayorCode,
                    inNetwork: selfPayPayor.inNetwork,
                    payersList: selfPayPayor.facilityPayorPlans,
                };

                plan = getPlanDetails(selfPayPayor, facilityPayers);
            }
        }

        vob.payor = payor;
        vob.plan = plan;
        vob.client = client;
        vob.facility = facility;
        // if conditions are met change payor to self-pay
        // if self pay, keep the vob as a self pay vob from 'createSeflPayVob'
        // if (vob.payor.payorId === 97) {
        //     vob = admissionsAdvisorUtils.createSelfPayVob(
        //         vobResult?.payload.advisorPatientId,
        //         vob,
        //         vobClassificationsState[0]
        //     );
        // }

        // Save VOB in FinPass if changes were made
        // Ready to Save the Estimate changes *if change was made in CRM
        let vobReSaveResponse: any = undefined;
        if (handleChange && !isConverted && confirmPtChanges) {
            vobReSaveResponse = await dispatch(saveSelectedVob({ 
                vobId: vob.vobId!, 
                vob: vob 
            }));
            let insuranceToSave: PatientCurrentInsurance = {
                ...emptyPatientCurrentInsurance,
                patientId: vobResult?.payload.patientId,
                payorId: vob.payor.payorId!,
                payorPlanId: vob.plan.payorPlanId!,
                groupNumber: vob.groupNum!,
                memberId: vob.policyNum!
            };
            await dispatch(savePatientCurrentInsurance({
                insurance: {
                    ...insuranceToSave,
                    patientId: vobResult?.payload.patientId,
                },
            }));

            let putEstimateBody = admissionsAdvisorUtils.mapToNewv3Estimate(
                vobPayload.vobId!,
                savedClientId,
                newFacilityId,
                updatedEstimate,
                vobResult?.payload.advisorPatientId,
                vobResult?.payload.patientId,
                {
                    payor: {
                        payorId: vob.payor.payorId,
                        facilityPayorId: vob.payor.facilityPayorId,
                        payorName: vob.payor.payorName,
                        facilityPayorName: vob.payor.facilityPayorName || vob.payor.payorName,
                        inNetwork: vob.payor.inNetwork || false
                    },
                    plan: {
                        payorPlanId: vob.plan.payorPlanId,
                        facilityPlanId: vob.plan.facilityPlanId,
                        planName: vob.plan.planName,
                        facilityPlanName: vob.plan.facilityPlanName || vob.plan.planName
                    },
                    isOverridden: false
                },
                instanceOfCare.entity.patientEncounterId
            );
            const payload = {
                estimate: putEstimateBody,
                estimateId: updatedEstimate.estimateId,
            };
            await dispatch(
                payor.payorId === 97
                    ? saveSelectedEstimateSelfPay(payload)
                    : saveSelectedEstimate(payload)
            );
        }

        const getPatientInfo = await dispatch(
            getPatientDemographics(instanceOfCare.entity.patientId)
        );
        const patientContact: any = getPatientInfo.payload;

        if (
            (!patientContact.patientDetails.contact.firstName &&
                !ptRecordClone.patient.contact.firstName) ||
            (!patientContact.patientDetails.contact.lastName &&
                !ptRecordClone.patient?.contact.lastName) ||
            (!patientContact.patientDetails.contact.birthDate &&
                !ptRecordClone.patient.contact.birthDate)
        ) {
            return handleGenericError(
                crmInboundErrorMessages.patientInfoError,
                dispatch,
                navigate
            );
        }
        dispatch(setSelectedEncounter(instanceOfCare.entity));

        // If a Change was detected on launch - we need to resave IOC
        if (handleChange && !isConverted && confirmPtChanges) {
            let instanceOfCareSave = {...instanceOfCare.entity};
            const vobPayloadFormat = {vob: vob};

            const iocConfig = mapToIOCModel(
                vobPayloadFormat,
                updatedEstimate,
                instanceOfCareSave.patientId,
                updatedEstimate.estimateId,
                ptRecordClone,
                queryParams,
                userProfile,
                clientsLeadId,
                clientsAccountId
            );

            // Saving IOC Changes in FinPass
            await dispatch(createPatientIoc(iocConfig));
        }

        let redirectPath: string;
        let isValid: boolean = false;

        // Use the most recent VOB response to determine validity
        if (vobReSaveResponse !== undefined) {
            isValid = vobReSaveResponse.payload.isValid;
        } else {
            isValid = vobPayload.isValid;
        }
        const launchClientId = Number(vob.client?.clientId ?? savedClientId);
        const launchClientFromMap = allClients.get(launchClientId);
        const isBillingEngineV3 =
            launchClientFromMap?.billingEngineVersion?.toUpperCase() === 'V3';
        const {patientId, patientEncounterId} = instanceOfCare.entity;
        const hubIocBase = `/hub/patient/${patientId}/ioc/${patientEncounterId}`;
        const hubChQuery = '?ch=crm';

        if (vobPayload?.vobId === undefined || isValid) {
            if (isBillingEngineV3 && opPay) {
                redirectPath = `${hubIocBase}${hubChQuery}`;
            } else {
                redirectPath = `/patients/${patientId}/estimate/${updatedEstimate.estimateId}${opPay ? '?op=pay' : ''}`;
            }
        } else {
            redirectPath = `/patients/${patientId}/vob/${vobPayload.vobId}${opPay ? '?op=pay' : ''}`;
        }

        window.location.href = redirectPath; // redirect with hard reload
    }
};

function formatVOBComments(
    ptRecordClone: any
): patientNotes[] | undefined {
    return ptRecordClone.vob?.comments?.length
        ? ptRecordClone.vob.comments.map((item: any) => ({
              noteText: item.comment,
              noteDt: '',
          }))
        : undefined;
}

export const getSelfPayPayer = (facilityPayers: any) => {
    return facilityPayers?.payload?.find(
        (row: any) => row.cfgPayorId === 97
    );
};

export const getPlanDetails = (selfPayPayer: any, facilityPayers: any) => {
    const [plan] = selfPayPayer?.facilityPayorPlans || [];
    return {
        ...plan,
        payorPlanId: plan?.cfgPayorPlanId,
        planName: plan?.facilityPayorPlanName,
        planCode: plan?.facilityPayorPlanCode,
        facilityPlanId: plan?.facilityPayorPlanId,
        pdrRate: (plan?.facilityPayorLocRateLos as PayorLocRateLos)?.pdrRate,
        losDays: (plan?.facilityPayorLocRateLos as PayorLocRateLos)?.losDays,
        payersPlanList: facilityPayers.payload,
    };
};

function mapToIOCModel(
    vobPatientRecord: any,
    estimatorState: any,
    patientId: number,
    estimateId: number,
    ptRecord: any,
    queryParams: any,
    userProfile: UserProfile,
    clientsLeadId: string,
    clientsAccountId: string
): IocConfig {
    const ptRecordTimingRiskId = ptRecord.timingRisk?.timingRiskId || 1;
    const currentTimingRisk: TimingRisk = {
        timingRiskId: ptRecordTimingRiskId,
        timingRiskName: timingRiskNameLookup(ptRecordTimingRiskId)!,
    };

    const emptyPayorRisk: PayorRisk = {
        payorRiskId: 1,
        payorRiskName: '',
    };

    let currentTypeOfCare: TypeOfCare | undefined = undefined;
    if (ptRecord.typeOfCare?.typeOfCareId > 0) {
        currentTypeOfCare = typeOfCare.find(
            item => item.typeOfCareId === ptRecord.typeOfCare?.typeOfCareId
        );
    }

    const urlParams = new URLSearchParams(queryParams);
    const formedParams = Object.fromEntries(urlParams);

    let wfStatus;
    const iocRoleStatus = hasIOCRole(userProfile);
    const estimateRoleStatus = hasEstimatesRole(userProfile);

    if (formedParams.dst === 'fp' || (!estimateRoleStatus && iocRoleStatus)) {
        wfStatus = workFlowStatus.preconvert.find((workflow: any) => {
            return (
                workflow.workflowStatus.workflowStatusName === 'New' &&
                workflow.workflowSubStatus.workflowSubStatusName === 'None'
            );
        });
    } else {
        wfStatus = {
            workflowId: 2,
            workflowStatus: {
                workflowStatusId: 20,
            },
            workflowSubStatus: {
                workflowSubStatusId: 7,
            },
        };
    }

    const pfrAmt =
        formedParams.dst === 'fp' && ptRecord.hasOwnProperty('pfr')
            ? ptRecord.pfr
            : estimatorState?.summary?.totalPFR ?? 0;

    const iocConfig: IocConfig = {
        paramId: 0,
        patientId: patientId,
        clientId:
            vobPatientRecord?.vob?.client?.clientId! ||
            vobPatientRecord?.vob.fpClientId,
        facilityId:
            vobPatientRecord?.vob?.facility?.facilityId! ||
            vobPatientRecord?.vob.fpClientFacilityId,
        pfrAmt: pfrAmt,
        timingRisk: currentTimingRisk,
        payorRisk: emptyPayorRisk,
        clientsPatientIOCId: clientsLeadId,
        clientsPatientAccountId: clientsAccountId,
        pfrEstimateId: estimateId,
        patientInsurance: ptRecord.patientInsurance,
        admissionDt: ptRecord.admissionDt,
        dischargeDt: ptRecord.dischargeDt,
        ...(currentTypeOfCare && {typeOfCare: currentTypeOfCare}),
        workflow: wfStatus,
    };

    return iocConfig;
}

function hasIOCRole(userProfile: UserProfile): boolean {
    const hasUserRolePage = userProfile.userRole.userRolePages.some(
        (page: UserRolePage) =>
            page?.sysRolePage?.rolePageName === RolePageNames.PatientRecords
    );
    return hasUserRolePage;
}

function hasEstimatesRole(userProfile: UserProfile): boolean {
    return checkUserRolePage(
        userProfile.userRole.userRolePages,
        RolePageNames.Estimates
    );
}

function checkForChanges(
    currentVOBFacilityId: number,
    currentPtRFacilityId: number,
    currentPtRPayor: string,
    currentVOBPayor: string
): {facilityChange: boolean; payerChange: boolean} {
    const facilityChange =
        currentVOBFacilityId !== currentPtRFacilityId &&
        currentVOBFacilityId !== undefined &&
        currentPtRFacilityId !== undefined;

    const payerChange =
        currentVOBPayor !== currentPtRPayor &&
        currentVOBPayor !== undefined &&
        currentPtRPayor !== undefined;

    return {
        facilityChange,
        payerChange,
    };
}

type ModalResponse = {
    confirmed: boolean;
};

